<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'NotificationItem',
  components: {
    FontAwesomeIcon,
  },
  props: {
    notification: {
      type: Object,
      required: true,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isUnread() {
      return !this.notification.read_at;
    },
    priorityClass() {
      const classes = {
        1: 'border-left-secondary',
        2: 'border-left-warning',
        3: 'border-left-danger',
        4: 'border-left-danger border-left-thick'
      };
      return classes[this.notification.priority] || 'border-left-secondary';
    },
    categoryBadgeVariant() {
      const variants = {
        system: 'info',
        reminder: 'warning',
        alert: 'danger',
        info: 'primary'
      };
      return variants[this.notification.category] || 'secondary';
    },
    notificationIcon() {
      const icons = {
        job_overdue: 'exclamation-triangle',
      };
      return icons[this.notification.type] || 'bell';
    },
    timeAgo() {
      const date = new Date(this.notification.created_at);
      const now = new Date();
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));

      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
      if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
      return `${Math.floor(diffInMinutes / 1440)}d ago`;
    },
    hasGoToAction() {
      return this.notification.action_buttons &&
             this.notification.action_buttons.some(action => action.action === 'navigate');
    },
    goToRoute() {
      if (!this.hasGoToAction) return null;

      const goToAction = this.notification.action_buttons.find(action => action.action === 'navigate');
      const url = goToAction.url;

      // Convert backend URL to Vue router route
      if (url.includes('/jobs/')) {
        const jobMatch = url.match(/\/jobs\/(\d+)/);
        const jobId = jobMatch && jobMatch[1] ? jobMatch[1] : null;
        return jobId ? { name: 'jobs.edit', params: { id: jobId } } : null;
      }
      if (url.includes('/reports/')) {
        const reportMatch = url.match(/\/reports\/(\d+)/);
        const reportId = reportMatch && reportMatch[1] ? reportMatch[1] : null;
        return reportId ? { name: 'reports.edit', params: { id: reportId } } : null;
      }
      if (url.includes('/expenses/')) {
        const expenseMatch = url.match(/\/expenses\/(\d+)/);
        const expenseId = expenseMatch && expenseMatch[1] ? expenseMatch[1] : null;
        return expenseId ? { name: 'expenses.edit', params: { id: expenseId } } : null;
      }
      if (url.includes('/cars/')) {
        const carMatch = url.match(/\/cars\/(\d+)/);
        const carId = carMatch && carMatch[1] ? carMatch[1] : null;
        return carId ? { name: 'cars.edit', params: { id: carId } } : null;
      }

      // news support
      if (url.includes('/news/')) {
        const newsMatch = url.match(/\/news\/(\d+)/);
        const newsId = newsMatch && newsMatch[1] ? newsMatch[1] : null;
        return newsId ? { name: 'news.show', params: { id: newsId } } : null;
      }

      return null;
    }
  },
  methods: {
    async markAsRead() {
      if (this.isUnread) {
        this.$emit('mark-read', this.notification.id);
        await this.$store.dispatch('notifications/markAsRead', this.notification.id);
      }
    },
    async dismiss() {
      this.$emit('dismiss', this.notification.id);
      await this.$store.dispatch('notifications/dismissNotification', this.notification.id);
    },
    async snooze(duration = 'P1D') {
      this.$emit('snooze', this.notification.id);
      await this.$store.dispatch('notifications/snoozeNotification', {
        notificationId: this.notification.id,
        duration
      });
    },
    goTo() {
      if (this.goToRoute) {
        this.$router.push(this.goToRoute);
        // @todo for now we will remove the notification - later it might just be "mark as read"
        this.dismiss();
        this.$emit('navigate');
      }
    },
    onNotificationClick() {
      // do nothing for now
      // if (!this.isUnread) return;
      // this.markAsRead();
    }
  },
};
</script>

<template>
  <div
    :class="[
      'notification-item d-flex align-items-start p-3 border-bottom',
      priorityClass,
      { 'bg-light': isUnread, 'cursor-pointer': isUnread },
      { 'p-2': compact }
    ]"
    @click="onNotificationClick"
  >
    <!-- Icon -->
    <div class="notification-icon mr-3 flex-shrink-0">
      <FontAwesomeIcon
        :icon="['fa', notificationIcon]"
        :class="[
          'fa-lg',
          { 'text-muted': !isUnread },
          { 'text-warning': notification.priority === 2 && isUnread },
          { 'text-danger': notification.priority >= 3 && isUnread }
        ]"
      />
    </div>

    <!-- Content -->
    <div class="notification-content flex-grow-1 min-width-0">
      <!-- Header -->
      <div class="d-flex align-items-center justify-content-between mb-1">
        <h6 :class="['mb-0', { 'font-weight-bold': isUnread }]">
          {{ notification.title }}
        </h6>
        <div class="d-flex align-items-center">
          <!-- Category Badge -->
          <b-badge
            v-if="!compact"
            :variant="categoryBadgeVariant"
            class="mr-2"
          >
            {{ notification.category }}
          </b-badge>

          <!-- Time -->
          <small class="text-muted">{{ timeAgo }}</small>
        </div>
      </div>

      <!-- Message -->
      <p :class="['mb-1', 'small', { 'text-muted': !isUnread }]">
        {{ notification.message }}
      </p>

      <!-- Actions -->
      <div v-if="showActions && !compact" class="notification-actions">
        <b-button-group size="sm">
          <!-- Go To Button -->
          <b-button
            v-if="hasGoToAction"
            variant="primary"
            size="sm"
            @click.stop="goTo"
          >
            <FontAwesomeIcon :icon="['fa', 'external-link-alt']" class="mr-1" />
            Go to
          </b-button>

          <!-- Dismiss Button -->
          <b-button
            variant="outline-secondary"
            size="sm"
            :disabled="!isUnread"
            @click.stop="dismiss"
          >
            <FontAwesomeIcon :icon="['fa', 'check']" class="mr-1" />
            {{ isUnread ? $t('actions.dismiss') : $t('actions.dismissed') }}
          </b-button>

          <!-- Snooze Button (for high priority) -->
          <b-dropdown
            v-if="notification.priority >= 2 && isUnread"
            size="sm"
            variant="outline-warning"
            text="Snooze"
            @click.stop
          >
            <b-dropdown-item @click="snooze('PT1H')">1 hour</b-dropdown-item>
            <b-dropdown-item @click="snooze('PT4H')">4 hours</b-dropdown-item>
            <b-dropdown-item @click="snooze('P1D')">1 day</b-dropdown-item>
            <b-dropdown-item @click="snooze('P3D')">3 days</b-dropdown-item>
            <b-dropdown-item @click="snooze('P1W')">1 week</b-dropdown-item>
          </b-dropdown>
        </b-button-group>
      </div>

      <!-- Compact Actions -->
      <div v-else-if="showActions && compact" class="notification-actions-compact mt-1">
        <b-button
          v-if="hasGoToAction"
          variant="link"
          size="sm"
          class="p-0 mr-2"
          @click.stop="goTo"
        >
          Go to
        </b-button>
        <b-button
          variant="link"
          size="sm"
          class="p-0"
          :disabled="!isUnread"
          @click.stop="dismiss"
        >
          {{ isUnread ? 'Dismiss' : 'Dismissed' }}
        </b-button>
      </div>
    </div>

    <!-- Unread Indicator -->
    <div v-if="isUnread" class="notification-unread-indicator ml-2">
      <span class="badge badge-primary badge-pill">&nbsp;</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.notification-item {
  transition: background-color 0.2s ease;

  &.cursor-pointer:hover {
    background-color: #f8f9fa !important;
  }

  &.border-left-secondary {
    border-left: 3px solid #6c757d;
  }

  &.border-left-warning {
    border-left: 3px solid #ffc107;
  }

  &.border-left-danger {
    border-left: 3px solid #dc3545;
  }

  &.border-left-thick {
    border-left-width: 5px;
  }
}

.notification-unread-indicator {
  .badge {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    padding: 0;
  }
}

.min-width-0 {
  min-width: 0;
}
</style>
